import React, { useEffect, useState } from 'react'

import {
  Button,
  BUTTON_THEME,
  Input,
  Select,
  BUTTON_COLOR,
} from '@st-operations/uikit/src/v2/index.js'

import RadioGroup from '_components/radio-input-group'
import { useDestinations } from '_hooks'
import { getCustomAudienceEnum } from '_services/audience'
import { getTaxonomies } from '_services/taxonomies'

import ProgressBar from '../components/progress-bar'
import { COLLECTIONS, getCollection } from '../collections'
import { fetchAllAudiences, fetchAudiencesByIds } from '../lib/load-audiences'
import { MODE_OPTIONS, QUERY_MODES, parseIds } from '../lib/query-modes'
import { downloadSessionReport } from '../lib/report'
import { countByStatus, deleteSession, getFailedIds, getSessions } from '../lib/session-store'

const SESSION_STATUS_LABELS = {
  draft: 'Not run',
  running: 'Interrupted',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
  aborted: 'Aborted',
}

const EMPTY_FILTERS = {
  created_by: null,
  destination: null,
  status: null,
  vertical: null,
  q: '',
}

const SessionItem = ({ session, disabled, onLoad, onDelete, onReport }) => {
  const counts = countByStatus(session)
  const failedCount = getFailedIds(session).length
  const hasRun = session.status !== 'draft'

  return (
    <li className="bulk-edit-sessions__item">
      <div className="bulk-edit-sessions__info">
        <strong>
          {new Date(session.created_at).toLocaleString()} ·{' '}
          {getCollection(session.collection).label}
        </strong>
        <span className="bulk-edit-sessions__query" title={session.query_label}>
          {session.query_label}
        </span>
        <span>
          {session.ids.length} audiences · {SESSION_STATUS_LABELS[session.status]}
          <If condition={hasRun}>
            {' '}
            · {counts.success || 0} updated · {failedCount} failed
          </If>
        </span>
      </div>
      <div className="bulk-edit-sessions__actions">
        <If condition={hasRun && failedCount > 0}>
          <Button theme={BUTTON_THEME.SECONDARY} disabled={disabled} onClick={onLoad(true)}>
            Retry failures ({failedCount})
          </Button>
        </If>
        <Button theme={BUTTON_THEME.SECONDARY} disabled={disabled} onClick={onLoad(false)}>
          Reload all
        </Button>
        <If condition={hasRun}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={onReport}>
            Report
          </Button>
        </If>
        <Button theme={BUTTON_THEME.SECONDARY} color={BUTTON_COLOR.RED} onClick={onDelete}>
          Delete
        </Button>
      </div>
    </li>
  )
}

const QueryStage = ({ onLoaded }) => {
  const [collectionId, setCollectionId] = useState(COLLECTIONS['custom-audiences'].id)
  const [taxonomy, setTaxonomy] = useState(null)
  const [taxonomies, setTaxonomies] = useState([])
  const [mode, setMode] = useState(QUERY_MODES.FILTERS)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [idsText, setIdsText] = useState('')
  const [createdByOptions, setCreatedByOptions] = useState([])
  const [verticalOptions, setVerticalOptions] = useState([])
  const [progress, setProgress] = useState(null)
  const [sessions, setSessions] = useState(getSessions())

  const destinations = useDestinations()
  const collection = getCollection(collectionId)
  const isCustomAudiences = collectionId === COLLECTIONS['custom-audiences'].id
  const isLoading = progress !== null

  useEffect(() => {
    getCustomAudienceEnum('created_by.email')
      .then(setCreatedByOptions)
      .catch(() => {})
    getCustomAudienceEnum('vertical')
      .then((items) => setVerticalOptions(items.filter(Boolean)))
      .catch(() => {})
    getTaxonomies({ fields: ['_id', 'name', 'slug'], limit: 1000, sort: 'name,1' })
      .then(({ items }) => setTaxonomies(items))
      .catch(() => toast.error('Error loading taxonomies.'))
  }, [])

  const setFilter = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }))

  const handleCollectionChange = (id) => {
    setCollectionId(id)
    setFilters(EMPTY_FILTERS)
  }

  // the local Radio reports the selected option by its label
  const handleCollectionRadio = (event) => {
    const selected = Object.values(COLLECTIONS).find(({ label }) => label === event.target.value)
    if (selected) handleCollectionChange(selected.id)
  }

  const handleModeRadio = (event) => {
    const selected = MODE_OPTIONS.find(({ label }) => label === event.target.value)
    if (selected) setMode(selected.value)
  }

  const collectionRadioOptions = Object.values(COLLECTIONS).map(({ id, label }) => ({
    label,
    checked: collectionId === id,
    disabled: isLoading,
  }))

  const modeRadioOptions = MODE_OPTIONS.map(({ value, label }) => ({
    label,
    checked: mode === value,
    disabled: isLoading,
  }))

  const buildContext = () => {
    const context = { filters: {} }
    if (isCustomAudiences) {
      if (filters.created_by) context.filters['created_by.email'] = filters.created_by.value
      if (filters.destination) context.filters['destinations._id'] = filters.destination._id
      if (filters.vertical) context.filters.vertical = filters.vertical.value
    } else {
      context.taxonomy = { _id: taxonomy._id, name: taxonomy.name, slug: taxonomy.slug }
    }
    if (filters.status) context.filters.status = filters.status.value
    if (filters.q.trim()) context.filters.q = filters.q.trim().toLowerCase()
    return context
  }

  const buildQueryLabel = () => {
    const parts = [
      !isCustomAudiences && taxonomy && `taxonomy "${taxonomy.name}"`,
      filters.created_by && `created by ${filters.created_by.value}`,
      filters.destination && `destination ${filters.destination.name}`,
      filters.status && `status ${filters.status.value}`,
      filters.vertical && `vertical ${filters.vertical.value}`,
      filters.q.trim() && `search "${filters.q.trim()}"`,
    ].filter(Boolean)
    return parts.length ? parts.join(', ') : 'all audiences'
  }

  const finishLoad = ({ loadedCollection, query, queryLabel, audiences, operations }) => {
    setProgress(null)
    if (!audiences.length) {
      toast.error('No audiences found for this query.')
      return
    }
    onLoaded({ collection: loadedCollection, query, queryLabel, audiences, operations })
  }

  const handleRunQuery = async () => {
    if (mode === QUERY_MODES.FILTERS && !isCustomAudiences && !taxonomy) {
      toast.error('Select a taxonomy to search in.')
      return
    }

    setProgress({ loaded: 0, total: null })
    try {
      if (mode === QUERY_MODES.IDS) {
        const ids = parseIds(idsText)
        if (!ids.length) {
          setProgress(null)
          toast.error('Paste at least one audience ID.')
          return
        }
        const { audiences, missing } = await fetchAudiencesByIds(collection, ids, setProgress)
        if (missing.length) {
          toast.error(`${missing.length} ID(s) could not be loaded and were ignored.`)
        }
        finishLoad({
          loadedCollection: collection,
          query: { ids },
          queryLabel: `id list (${ids.length} ids)`,
          audiences,
        })
      } else {
        const context = buildContext()
        const audiences = await fetchAllAudiences(collection, context, setProgress)
        finishLoad({
          loadedCollection: collection,
          query: context,
          queryLabel: buildQueryLabel(),
          audiences,
        })
      }
    } catch (error) {
      setProgress(null)
      toast.error(error?.message || 'Error loading audiences.')
    }
  }

  const handleLoadSession = (session, onlyFailed) => async () => {
    const sessionCollection = getCollection(session.collection)
    const ids = onlyFailed ? getFailedIds(session) : session.ids
    setProgress({ loaded: 0, total: ids.length })
    try {
      const { audiences, missing } = await fetchAudiencesByIds(sessionCollection, ids, setProgress)
      if (missing.length) {
        toast.error(`${missing.length} ID(s) could not be loaded and were ignored.`)
      }
      const date = session.created_at.split('T')[0]
      finishLoad({
        loadedCollection: sessionCollection,
        query: { ids },
        queryLabel: `${onlyFailed ? 'retry of failures' : 'reload'} from ${date} session`,
        audiences,
        operations: session.operations,
      })
    } catch (error) {
      setProgress(null)
      toast.error(error?.message || 'Error loading audiences.')
    }
  }

  const handleDeleteSession = (id) => () => {
    deleteSession(id)
    setSessions(getSessions())
  }

  return (
    <div className="bulk-edit-query">
      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>1. Find the audiences to update</h3>
        </header>

        <div className="bulk-edit-query__selectors">
          <div className="bulk-edit-query__selector">
            <span className="bulk-edit-query__selector-label">Search in:</span>
            <RadioGroup
              groupName="bulk-edit-collection"
              className="bulk-edit-query__radios"
              options={collectionRadioOptions}
              onChange={handleCollectionRadio}
            />
          </div>
          <div className="bulk-edit-query__selector">
            <span className="bulk-edit-query__selector-label">By:</span>
            <RadioGroup
              groupName="bulk-edit-mode"
              className="bulk-edit-query__radios"
              options={modeRadioOptions}
              onChange={handleModeRadio}
            />
          </div>
        </div>

        <Choose>
          <When condition={mode === QUERY_MODES.FILTERS}>
            <p className="bulk-edit__hint">
              The query loads every matching page (not just the first one) into your browser, with
              only the fields the bulk edit needs.
            </p>
            <div className="bulk-edit-query__filters">
              <If condition={!isCustomAudiences}>
                <Select
                  label="Taxonomy"
                  useReactSelect
                  isClearable
                  isSearchable
                  options={taxonomies}
                  value={taxonomy}
                  onChange={setTaxonomy}
                />
              </If>
              <Input
                label="Search"
                placeholder="Search for audiences"
                value={filters.q}
                onChange={setFilter('q')}
              />
              <If condition={isCustomAudiences}>
                <Select
                  label="Created by"
                  useReactSelect
                  isClearable
                  isSearchable
                  options={createdByOptions}
                  value={filters.created_by}
                  onChange={setFilter('created_by')}
                />
                <Select
                  label="Destination"
                  useReactSelect
                  isClearable
                  isSearchable
                  options={destinations}
                  value={filters.destination}
                  onChange={setFilter('destination')}
                />
              </If>
              <Select
                label="Status"
                useReactSelect
                isClearable
                options={collection.statuses}
                value={filters.status}
                onChange={setFilter('status')}
              />
              <If condition={isCustomAudiences}>
                <Select
                  label="Vertical"
                  useReactSelect
                  isClearable
                  isSearchable
                  options={verticalOptions}
                  value={filters.vertical}
                  onChange={setFilter('vertical')}
                />
              </If>
            </div>
          </When>
          <Otherwise>
            <p className="bulk-edit__hint">
              Paste audience IDs separated by spaces, commas or line breaks. Useful for retrying a
              specific subset (e.g. the failures of a previous run).
            </p>
            <textarea
              className="bulk-edit-query__ids"
              rows={6}
              placeholder={'5eea1b7f37097dac6c04fa12\n632dec466420cb001a723e60'}
              value={idsText}
              onChange={(event) => setIdsText(event.target.value)}
            />
          </Otherwise>
        </Choose>

        <footer className="bulk-edit__card-footer">
          <If condition={isLoading}>
            <ProgressBar loaded={progress.loaded} total={progress.total} label="Loaded" />
          </If>
          <Button onClick={handleRunQuery} disabled={isLoading}>
            {isLoading ? 'Loading audiences…' : 'Run Query'}
          </Button>
        </footer>
      </section>

      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>Previous bulk edits</h3>
        </header>
        <p className="bulk-edit__hint">
          Runs are tracked in this browser only (localStorage). Use them to download reports or
          retry failures without re-running the original query.
        </p>
        <Choose>
          <When condition={sessions.length === 0}>
            <p className="bulk-edit__empty">No bulk edits recorded on this browser yet.</p>
          </When>
          <Otherwise>
            <ul className="bulk-edit-sessions">
              <For each="session" of={sessions}>
                <SessionItem
                  key={session.id}
                  session={session}
                  disabled={isLoading}
                  onLoad={(onlyFailed) => handleLoadSession(session, onlyFailed)}
                  onDelete={handleDeleteSession(session.id)}
                  onReport={() => downloadSessionReport(session)}
                />
              </For>
            </ul>
          </Otherwise>
        </Choose>
      </section>
    </div>
  )
}

export default QueryStage
